import { useCallback } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import { useToast } from '@cbhq/cds-web/overlays/useToast';

import { useStepUpSubmission } from '../../../hooks/useStepUpSubmission';

import { useHandleInviteFailure } from './useHandleInviteFailure';
import type { UseInviteSubmissionParams } from './useInviteSubmission';
import { useInviteSubmission } from './useInviteSubmission';

type InviteSendMessages = {
  inviteSuccess: MessageDescriptor;
  inviteError: MessageDescriptor;
};

type UseInviteSendParams = Omit<UseInviteSubmissionParams, 'onSuccess' | 'onFailure'> & {
  isGatedRoleMutation: boolean;
  messages: InviteSendMessages;
  /** Receives the inline email error for a RESTRICTED_DOMAIN_MISMATCH rejection. */
  setEmailError: (message: string | null) => void;
  onClose: () => void;
};

/**
 * Sending an invite from `MultiLEInviteTray` or `SingleLEInviteTray`: the
 * step-up challenge, the success toast, and failure handling through
 * `useHandleInviteFailure`. A sent invite closes the tray.
 */
export function useInviteSend({
  isGatedRoleMutation,
  messages,
  setEmailError,
  onClose,
  ...submission
}: UseInviteSendParams) {
  const { formatMessage } = useIntl();
  const toast = useToast();

  const { submit, stepUpModalProps, resetStepUpRecovery, handleStepUpProofTokenError } =
    useStepUpSubmission({ isGatedRoleMutation });

  const handleInviteSuccess = useCallback(() => {
    resetStepUpRecovery();
    toast.show(formatMessage(messages.inviteSuccess));
    onClose();
  }, [formatMessage, messages.inviteSuccess, onClose, resetStepUpRecovery, toast]);

  const surfaceInviteFailure = useHandleInviteFailure({
    setEmailError,
    toast,
    genericErrorMessage: messages.inviteError,
  });

  const handleInviteFailure = useCallback(
    (error: unknown) => {
      // A rejected step-up proof token re-opens the 2FA challenge, so it must be
      // checked before surfaceInviteFailure turns the error into user-facing copy.
      if (handleStepUpProofTokenError(error)) {
        return;
      }
      surfaceInviteFailure(error);
    },
    [handleStepUpProofTokenError, surfaceInviteFailure],
  );

  const { isSubmitting, submitInvite } = useInviteSubmission({
    ...submission,
    onSuccess: handleInviteSuccess,
    onFailure: handleInviteFailure,
  });

  const sendInvite = useCallback(() => {
    submit(submitInvite);
  }, [submit, submitInvite]);

  return { isSubmitting, sendInvite, stepUpModalProps };
}
